import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Users } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUsers } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export const ReservedBadge = ({ testid }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-[#0A1128] px-2.5 py-1 text-[11px] font-bold text-white" title="Cliente riservato" data-testid={testid}>
    <Lock className="h-3 w-3" />Riservato
  </span>
);

export const ClientVisibility = ({ client, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: users } = useUsers();
  const [vis, setVis] = useState(client.visibilita || "tutti");
  const [membri, setMembri] = useState(client.membri || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVis(client.visibilita || "tutti"); setMembri(client.membri || []); }, [client]);
  const active = (users || []).filter((u) => u.is_active !== false);
  const hasVincenzo = (users || []).some((u) => membri.includes(u.id) && /^\s*vincenzo\b/i.test(u.nome || ""));
  const save = async () => {
    setSaving(true);
    try { const r = await api.patch(`/clients/${client.id}/visibilita`, { visibilita: vis, membri }); onChanged?.(r.data); window.dispatchEvent(new Event("core-clients-changed")); toast.success("Visibilità aggiornata"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const names = (users || []).filter((u) => (client.membri || []).includes(u.id)).map((u) => `${u.nome} ${u.cognome}`);
  return (
    <div className="mt-6 rounded-2xl border border-[#5FB3EA] bg-[#A6DAFC]/50 p-4" data-testid="client-visibility">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2"><Lock className="h-4 w-4" />Visibilità</h3>
      {!isAdmin ? (
        <p className="mt-2 text-sm text-slate-700" data-testid="client-visibility-readonly">
          {client.visibilita === "riservato" ? `Cliente riservato. Membri: ${names.join(", ") || "—"}.` : "Visibile a tutti i soci."}
        </p>
      ) : (
        <div className="mt-3 grid gap-3">
          <div className="flex flex-wrap gap-2">
            {[["tutti", "Visibile a tutti i soci", Users], ["riservato", "Riservato ai membri", Lock]].map(([v, l, Icon]) => (
              <button key={v} type="button" onClick={() => setVis(v)} aria-pressed={vis === v} data-testid={`client-visibility-${v}`}
                className={`min-h-[44px] inline-flex items-center gap-1.5 rounded-full px-4 text-sm font-bold border transition-colors ${vis === v ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-[#0A1128] border-[#5FB3EA]"}`}>
                <Icon className="h-4 w-4" />{l}
              </button>
            ))}
          </div>
          {vis === "riservato" && (
            <div className="grid gap-2" data-testid="client-visibility-members">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Membri autorizzati (l'amministratore ha sempre accesso)</div>
              {active.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm font-semibold text-[#0A1128] min-h-[36px]">
                  <Checkbox checked={membri.includes(u.id) || u.role === "admin"} disabled={u.role === "admin"}
                    onCheckedChange={(on) => setMembri(on ? [...membri, u.id] : membri.filter((m) => m !== u.id))} data-testid={`client-visibility-member-${u.id}`} />
                  {u.nome} {u.cognome}{u.role === "admin" && <span className="text-xs text-slate-500">(amministratore)</span>}
                </label>
              ))}
              {client.nota_visibilita && !hasVincenzo && (
                <div className="rounded-xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs font-bold text-[#0A1128]" data-testid="client-visibility-hint">{client.nota_visibilita}</div>
              )}
            </div>
          )}
          <div><Button onClick={save} disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="client-visibility-save">{saving ? "Salvataggio…" : "Salva visibilità"}</Button></div>
        </div>
      )}
    </div>
  );
};
